import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Send, Check, AlertCircle, Loader2, FileUp, X } from 'lucide-react';
import { jobs } from '../data/jobs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_CV_BYTES = 5 * 1024 * 1024;
const EMPTY = { firstName: '', lastName: '', email: '', phone: '', message: '', linkedin: '', rgpd: false, website: '' };

const inputCls = 'mt-2 w-full border-b border-black/20 focus:border-[#ff7300] outline-none py-3 text-[#010c23] bg-transparent';

const Field = ({ id, label, type = 'text', value, onChange, required, placeholder }) => (
  <div>
    <label htmlFor={id} className="text-xs uppercase tracking-widest text-[#010c23]/60">{label}{!required && <span className="normal-case tracking-normal text-[#010c23]/40"> (optionnel)</span>}</label>
    <input id={id} required={required} type={type} value={value} onChange={onChange} placeholder={placeholder} minLength={id === 'phone' ? 4 : undefined} className={inputCls} data-testid={`candidature-${id}`} />
  </div>
);

const PdfPicker = ({ id, label, optional, file, onPick, onClear, buttonText }) => {
  const ref = useRef(null);
  return (
    <div className="flex flex-col">
      <label className="text-xs uppercase tracking-widest text-[#010c23]/60 whitespace-nowrap">{label}{optional && <span className="normal-case tracking-normal text-[#010c23]/40"> (optionnel)</span>}</label>
      <input ref={ref} type="file" accept="application/pdf,.pdf" onChange={onPick} className="hidden" data-testid={`candidature-${id}-input`} />
      {file ? (
        <div className="mt-2 flex-1 flex items-center justify-between gap-3 border border-[#ff7300]/40 bg-[#ff7300]/5 px-4 py-3" data-testid={`candidature-${id}-selected`}>
          <span className="text-sm text-[#010c23] truncate">{file.name} <span className="text-[#010c23]/50">· {(file.size / 1024 / 1024).toFixed(2)} Mo</span></span>
          <button type="button" onClick={onClear} aria-label="Retirer le fichier" className="text-[#010c23]/60 hover:text-[#ff7300]" data-testid={`candidature-${id}-clear`}><X size={16} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} className="mt-2 flex-1 w-full flex items-center justify-center gap-2 border border-dashed border-black/25 hover:border-[#ff7300] text-sm text-[#010c23]/70 hover:text-[#ff7300] py-5 transition-colors" data-testid={`candidature-${id}-button`}>
          <FileUp size={16} /> {buttonText}
        </button>
      )}
    </div>
  );
};

const errorMessage = (detail, status, fallback) => {
  if (status === 429) return 'Trop de candidatures envoyées. Merci de réessayer un peu plus tard ou de nous écrire par e-mail.';
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) return 'Certains champs sont invalides. Merci de vérifier votre saisie.';
  return fallback;
};

const SubmitLabel = ({ state }) => {
  if (state === 'loading') return <><Loader2 size={18} className="animate-spin" /> Envoi…</>;
  if (state === 'success') return <><Check size={18} /> Candidature envoyée</>;
  return <>Envoyer ma candidature <Send size={18} /></>;
};

const StatusMessage = ({ status }) => {
  if (status.state === 'success') return <div className="mt-5 flex items-start gap-2 text-sm text-[#010c23]/80 bg-[#ff7300]/10 border border-[#ff7300]/30 p-4" data-testid="candidature-success"><Check size={16} className="mt-0.5 text-[#ff7300]" /><span>{status.msg}</span></div>;
  if (status.state === 'error') return <div className="mt-5 flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 p-4" data-testid="candidature-error"><AlertCircle size={16} className="mt-0.5" /><span>{status.msg}</span></div>;
  return null;
};

const validatePdf = (f, label) => {
  if (!f.name.toLowerCase().endsWith('.pdf') || (f.type && f.type !== 'application/pdf')) return `${label} doit être au format PDF.`;
  if (f.size > MAX_CV_BYTES) return `${label} dépasse 5 Mo. Merci de le compresser.`;
  return null;
};

const buildFormData = (form, offer, cv, letter) => {
  const fd = new FormData();
  const fields = { first_name: form.firstName, last_name: form.lastName, email: form.email, phone: form.phone, offer, message: form.message, linkedin: form.linkedin, website: form.website };
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
  fd.append('cv', cv);
  if (letter) fd.append('cover_letter', letter);
  return fd;
};

const successMessage = (ackSent) => `Votre candidature a bien été transmise.${ackSent ? ' Un accusé de réception vous a été adressé par e-mail.' : ''} Nous reviendrons vers vous dans les meilleurs délais.`;

const useCandidatureSubmit = ({ form, offer, cv, letter, onSuccess }) => {
  const [status, setStatus] = useState({ state: 'idle', msg: '' });
  const setError = (msg) => setStatus({ state: 'error', msg });

  const pickPdf = (setter, label) => (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const error = validatePdf(f, label);
    if (error) { setError(error); return; }
    setStatus({ state: 'idle', msg: '' });
    setter(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cv) { setError('Merci de joindre votre CV au format PDF.'); return; }
    if (!form.rgpd) { setError('Merci d’accepter le traitement de vos données.'); return; }
    setStatus({ state: 'loading', msg: '' });
    try {
      const { data } = await axios.post(`${API}/candidature`, buildFormData(form, offer, cv, letter));
      setStatus({ state: 'success', msg: successMessage(data.ack_sent) });
      onSuccess();
    } catch (err) {
      setError(errorMessage(err?.response?.data?.detail, err?.response?.status, 'Impossible d’envoyer la candidature. Réessayez ou écrivez-nous par e-mail.'));
    }
  };

  return { status, pickPdf, submit };
};

export const CandidatureForm = ({ offer, onOfferChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [cv, setCv] = useState(null);
  const [letter, setLetter] = useState(null);
  const reset = () => { setForm(EMPTY); setCv(null); setLetter(null); };
  const { status, pickPdf, submit: onSubmit } = useCandidatureSubmit({ form, offer, cv, letter, onSuccess: reset });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: k === 'rgpd' ? e.target.checked : e.target.value }));

  return (
    <form onSubmit={onSubmit} className="bg-white border border-black/5 p-8 md:p-12" data-testid="candidature-form">
      <input type="text" name="website" value={form.website} onChange={onChange('website')} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div>
        <label htmlFor="offer" className="text-xs uppercase tracking-widest text-[#010c23]/60">Offre concernée</label>
        <select id="offer" value={offer} onChange={(e) => onOfferChange(e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="candidature-offer">
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
          <option value="spontanee">Candidature spontanée</option>
        </select>
      </div>
      <div className="grid md:grid-cols-2 gap-5 mt-6">
        <Field id="first-name" label="Prénom" required value={form.firstName} onChange={onChange('firstName')} />
        <Field id="last-name" label="Nom" required value={form.lastName} onChange={onChange('lastName')} />
        <Field id="email" label="E-mail" type="email" required value={form.email} onChange={onChange('email')} />
        <Field id="phone" label="Téléphone" type="tel" required value={form.phone} onChange={onChange('phone')} />
      </div>
      <div className="mt-5">
        <Field id="linkedin" label="Profil LinkedIn" type="url" value={form.linkedin} onChange={onChange('linkedin')} placeholder="https://www.linkedin.com/in/…" />
      </div>
      <div className="mt-5">
        <label htmlFor="message" className="text-xs uppercase tracking-widest text-[#010c23]/60">Message / lettre de motivation <span className="normal-case tracking-normal text-[#010c23]/40">(optionnel)</span></label>
        <textarea id="message" maxLength={5000} value={form.message} onChange={onChange('message')} rows={5} className={`${inputCls} resize-none`} placeholder="Quelques lignes sur votre parcours et votre motivation…" data-testid="candidature-message" />
      </div>
      <p className="mt-6 text-xs text-[#010c23]/50">Documents au format PDF, 5 Mo maximum par fichier.</p>
      <div className="mt-3 grid md:grid-cols-2 gap-5">
        <PdfPicker id="cv" label="CV" file={cv} onPick={pickPdf(setCv, 'Le CV')} onClear={() => setCv(null)} buttonText="Sélectionner votre CV" />
        <PdfPicker id="letter" label="Lettre de motivation" optional file={letter} onPick={pickPdf(setLetter, 'La lettre de motivation')} onClear={() => setLetter(null)} buttonText="Joindre une lettre" />
      </div>
      <div className="flex items-start gap-3 mt-6">
        <input type="checkbox" required id="candidature-rgpd" checked={form.rgpd} onChange={onChange('rgpd')} className="mt-1 accent-[#ff7300]" data-testid="candidature-rgpd" />
        <label htmlFor="candidature-rgpd" className="text-xs text-[#010c23]/60 leading-relaxed">J’accepte que mes données et mes documents soient utilisés dans le cadre du traitement de ma candidature, conformément à la <Link to="/reglementaire#confidentialite" className="text-[#ff7300] hover:underline" data-testid="candidature-privacy-link">politique de confidentialité</Link> d’Ometis Patrimoine.</label>
      </div>
      <button type="submit" disabled={status.state === 'loading'} className="btn-primary mt-8 disabled:opacity-70" data-testid="candidature-submit">
        <SubmitLabel state={status.state} />
      </button>
      <StatusMessage status={status} />
    </form>
  );
};
